import { Helmet } from "react-helmet-async";
import StructuredData from "@/components/StructuredData";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PracticeTypeHero from "@/components/practiceType/PracticeTypeHero";
import PracticeRisks from "@/components/practiceType/PracticeRisks";
import RecommendedCoverage from "@/components/practiceType/RecommendedCoverage";
import PracticeTypeCaseStudies from "@/components/practiceType/PracticeTypeCaseStudies";
import PracticeTypeFAQ from "@/components/practiceType/PracticeTypeFAQ";
import ContactForm from "@/components/ContactForm";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { getPracticeType } from "@/utils/practiceTypeData";
import { getOrganizationSchema, getBreadcrumbSchema, getFAQPageSchema } from "@/utils/seoSchemas";

const HausarztPraxis = () => {
  const practiceType = getPracticeType("hausarzt")!;

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Home", url: "https://praxis-versichert.de" },
    { name: "Cyberversicherung", url: "https://praxis-versichert.de/cyberversicherung" },
    { name: "Hausarztpraxen" }
  ]);

  const faqSchema = getFAQPageSchema(
    practiceType.faqs.map(faq => ({
      question: faq.question,
      answer: faq.answer
    }))
  );

  return (
    <>
      <Helmet>
        <title>{practiceType.title}</title>
        <meta name="description" content={practiceType.metaDescription} />
        <link rel="canonical" href="https://praxis-versichert.de/cyberversicherung/hausarztpraxis" />
        
        <meta property="og:title" content={practiceType.title} />
        <meta property="og:description" content={practiceType.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://praxis-versichert.de/cyberversicherung/hausarztpraxis" />
        
        <script type="application/ld+json">
          {JSON.stringify(getOrganizationSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <StructuredData data={[getOrganizationSchema(), breadcrumbSchema, faqSchema]} />

      <Navigation />
      
      <main>
        <PracticeTypeHero practiceType={practiceType} />
        <PracticeRisks practiceType={practiceType} />
        <RecommendedCoverage practiceType={practiceType} />
        <PracticeTypeCaseStudies practiceType={practiceType} />
        <PracticeTypeFAQ practiceType={practiceType} />
        
        {/* New CTA Section with ContactForm */}
        <section className="py-20 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/15 via-accent/5 to-accent/15" />
          <motion.div 
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
          />
          
          <div className="container mx-auto max-w-5xl relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <ScrollAnimatedSection>
                <h2 className="text-3xl font-bold mb-4">Jetzt persönlich beraten lassen</h2>
                <p className="text-muted-foreground mb-6">
                  Unsere Experten helfen Ihnen, den optimalen Schutz für Ihre {practiceType.name.toLowerCase()} zu finden.
                </p>
                <Link 
                  to="/termin-buchen"
                  className="inline-flex items-center px-8 py-4 bg-transparent text-white border-2 border-accent rounded-md font-semibold transition-all hover:shadow-[0_0_25px_rgba(139,195,74,0.5)]"
                >
                  Jetzt Termin vereinbaren
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </ScrollAnimatedSection>
              
              <div className="bg-card/80 backdrop-blur-sm border border-accent/30 rounded-xl p-6 hover:border-accent/50 transition-colors">
                <h3 className="text-xl font-semibold mb-4 text-center">Beratung anfragen</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default HausarztPraxis;
